import {
  MietendeckelCaseFilter,
  MietminderungCaseFilter,
  MietpreisbremseCaseFilter,
} from "@happy-legal/graphql-types";
import {
  Button,
  Checkbox,
  Grid,
  InputAdornment,
  ListItemText,
  MenuItem,
  TextField,
} from "@mui/material";
import { ChangeEvent, Dispatch, SetStateAction, useContext } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ProductTypeEnum, StatusEnum } from "../enums";
import { hasDirtyFields } from "../helpers/hasDirtyFields";
import { CaseContext } from "../providers/CaseProvider";
import HLSelect from "./Form/HLSelect";
import HLTextField from "./Form/HLTextField";

interface ICaseFilter {
  filter:
    | MietminderungCaseFilter
    | MietendeckelCaseFilter
    | MietpreisbremseCaseFilter;
  setFilter: Dispatch<
    SetStateAction<
      | MietminderungCaseFilter
      | MietendeckelCaseFilter
      | MietpreisbremseCaseFilter
    >
  >;
  notEqual:
    | MietminderungCaseFilter
    | MietendeckelCaseFilter
    | MietpreisbremseCaseFilter;
  setNotEqual: Dispatch<
    SetStateAction<
      | MietminderungCaseFilter
      | MietendeckelCaseFilter
      | MietpreisbremseCaseFilter
    >
  >;
}

/**
 * Small form to allow for case filtering in table
 * @param filter object containing filter variables
 * @param setFilter state setter for above obj
 */
const CasesFilter = ({ filter, setFilter }: ICaseFilter) => {
  // Load reducer
  const { state } = useContext(CaseContext);
  const allCases = state?.allCases;

  // Partition key of current case type
  const partitionKey =
    !!allCases?.items?.length && allCases?.items[0]?.partitionKey;

  // Establish RHF
  const methods = useForm<any>({
    defaultValues: {
      cid: "",
      productType: "",
      profile: { firstName: "", lastName: "" },
      statusList: [],
    },
    shouldUnregister: false,
  });
  const { setValue, watch, reset } = methods;

  // Dependant field and setter fn for it
  const statusList = watch("statusList");
  const handleStatusList = (
    ev: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) =>
    setValue("statusList", ev.target.value, {
      shouldDirty: true,
      shouldValidate: true,
    });

  // Submission fn that creates correct input obj for setter
  // (parent has useEffect dependancy)
  const handleSubmit = (formData: any) => {
    const {
      cid,
      productType,
      profile: { firstName, lastName },
      statusList,
    } = formData;

    setFilter({
      ...(cid && { cid }),
      ...(productType && { productType }),
      ...((firstName || lastName) && {
        profile: {
          ...(firstName && { firstName }),
          ...(lastName && { lastName }),
        },
      }),
      ...(!!statusList.length && { statusList }),
    });
  };

  const handleReset = () => {
    reset();
    setFilter({});
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <HLTextField
              label="Fall-Nr."
              type="number"
              name="cid"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {partitionKey}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <HLSelect
              name="productType"
              label="Produkttyp"
              // Inverse the keys as values
              options={Object.entries(ProductTypeEnum).map(
                ([value, label]) => ({
                  label,
                  value,
                })
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <HLTextField label="Vorname" name="profile.firstName" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <HLTextField label="Nachname" name="profile.lastName" />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              select
              variant="outlined"
              fullWidth
              label="Status"
              InputLabelProps={{
                shrink: true,
              }}
              value={statusList}
              onChange={handleStatusList}
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: (selected) =>
                  (selected as StatusEnum[]).length
                    ? (selected as StatusEnum[])
                        //@ts-ignore - string index
                        .map((status) => StatusEnum[status])
                        .join(", ")
                    : "Alle",
              }}
            >
              {Object.entries(StatusEnum).map(([value, label]) => (
                <MenuItem value={value} key={label}>
                  <Checkbox size="small" checked={statusList.includes(value)} />
                  <ListItemText primary={label} />
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            size={12}
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
          >
            {!!Object.keys(filter)?.length && (
              <Button onClick={handleReset}>Zurücksetzen</Button>
            )}
            <Button
              variant="contained"
              type="submit"
              color="primary"
              disabled={!hasDirtyFields(methods) && !statusList?.length}
            >
              Filtern
            </Button>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default CasesFilter;
